/**
 * geocodeMatch: the one reading of an Azure Maps answer and of the words
 * an address is written in. parseGeocodeMatch reads a result, matchZips a
 * postal code list, listedTownNames the town names a result lists and
 * suggestedTownName the one town a suggestion names, canonicalRouteName a
 * route name, and STREET_RESULT_TYPES names the street-level result
 * types; the tables (STREET_SUFFIXES, STREET_WORDS, DIRECTIONALS,
 * ORDINALS, ROUTE_QUALIFIERS, ROUTE_WORDS, ROUTE_LEADS, TOWN_TOKENS,
 * UNIT_KEYWORDS, FLOOR_KEYWORDS) grow by entries only, each with its case
 * in the table's own test. isUnitSegment reads a unit line ("Suite 2",
 * "#4", "2nd Floor") and isPoBoxLine a PO Box line ("PO Box 705", "P.O.
 * Box 12", "Post Office Box 9").
 *
 * Pure, and imports nothing from the geocoder: the geocoder's placement
 * rule (azureMaps.ts, acceptance.ts), the split of stored text into its
 * parts (acceptance.ts) and the address check (addressCheck.ts, which
 * exports the tables and readers as its own) read the same tables and
 * readers from this one place. src/lib/address.ts carries the browser
 * twin of isPoBoxLine; tests/ui/formatAddressParity.test.ts keeps them
 * equal.
 */

import { US_STATE_CODES, isUsStateCode } from '../usStates';

/** What the check reads from Azure's first result. */
export interface GeocodeMatch {
  type: string | null;
  score: number | null;
  streetNumber: string | null;
  streetName: string | null;
  municipality: string | null;
  localName: string | null;
  /** The address's municipalitySubdivision, a part of a town. */
  municipalitySubdivision: string | null;
  /** The result's entityType, set on a Geography result ('Municipality',
   *  'PostalCodeArea' and the like); null when absent. */
  entityType: string | null;
  /** matchZips(postalCode): each entry's first five digits. */
  zips: string[];
  /** countrySubdivisionCode, trimmed and uppercased, when it is a US state
   *  or territory code; else null. */
  stateCode: string | null;
}

/** Azure Maps result types that mean a street matched: a street-level
 *  answer. Any other type (a Geography, a POI) is not. */
export const STREET_RESULT_TYPES: ReadonlySet<string> = new Set(['Point Address', 'Address Range', 'Street', 'Cross Street']);

// ---------------------------------------------------------------------
// The tables
// ---------------------------------------------------------------------

/** A street suffix spelling to its canonical form. */
export const STREET_SUFFIXES: ReadonlyMap<string, string> = new Map([
  ['street', 'st'],
  ['road', 'rd'],
  ['avenue', 'ave'],
  ['av', 'ave'],
  ['boulevard', 'blvd'],
  ['drive', 'dr'],
  ['lane', 'ln'],
  ['court', 'ct'],
  ['place', 'pl'],
  ['terrace', 'ter'],
  ['parkway', 'pkwy'],
  ['highway', 'hwy'],
  ['circle', 'cir'],
  ['trail', 'trl'],
  ['square', 'sq'],
  ['turnpike', 'tpke'],
  ['tpk', 'tpke'],
  ['route', 'rte'],
  ['rt', 'rte'],
  ['way', 'way'],
  ['crescent', 'cres'],
  ['point', 'pt'],
  ['heights', 'hts'],
  ['extension', 'ext'],
  ['pike', 'pike'],
  ['crossing', 'xing'],
  ['ridge', 'rdg'],
  ['hill', 'hl'],
  ['expressway', 'expy'],
  ['freeway', 'fwy'],
  ['alley', 'aly'],
  ['run', 'run'],
  ['loop', 'loop'],
]);

/** A word inside a street name to its canonical form. */
export const STREET_WORDS: ReadonlyMap<string, string> = new Map([
  ['saint', 'st'],
  ['fort', 'ft'],
  ['mount', 'mt'],
  ['mountain', 'mtn'],
]);

/** A compass word to its abbreviation. */
export const DIRECTIONALS: ReadonlyMap<string, string> = new Map([
  ['north', 'n'],
  ['south', 's'],
  ['east', 'e'],
  ['west', 'w'],
  ['northeast', 'ne'],
  ['northwest', 'nw'],
  ['southeast', 'se'],
  ['southwest', 'sw'],
]);

/** A spelled-out ordinal to its numeral. */
export const ORDINALS: ReadonlyMap<string, string> = new Map([
  ['first', '1st'],
  ['second', '2nd'],
  ['third', '3rd'],
  ['fourth', '4th'],
  ['fifth', '5th'],
  ['sixth', '6th'],
  ['seventh', '7th'],
  ['eighth', '8th'],
  ['ninth', '9th'],
  ['tenth', '10th'],
]);

/** A town word to its canonical form. */
export const TOWN_TOKENS: ReadonlyMap<string, string> = new Map([
  ['mount', 'mt'],
  ['saint', 'st'],
  ['fort', 'ft'],
  ['township', 'twp'],
  ['borough', 'boro'],
  ['heights', 'hts'],
]);

/** Phrases that open a route name, stored space-joined. A state or
 *  territory code opens one too, read from US_STATE_CODES. */
export const ROUTE_QUALIFIERS: ReadonlySet<string> = new Set(['us', 'us highway', 'state', 'county', 'co', 'cr', 'co rd']);

/** The word that may sit between a route's opening and its number. */
export const ROUTE_WORDS: ReadonlySet<string> = new Set(['route', 'rte', 'rt', 'highway', 'hwy', 'road', 'rd']);

/** A bare word that opens a route name with no qualifier. */
export const ROUTE_LEADS: ReadonlySet<string> = new Set(['route', 'rte', 'rt']);

/** A word that starts the unit tail of a street line (a trailing `.`
 *  ignored). A token starting with `#` starts one too, by its own rule. */
export const UNIT_KEYWORDS: ReadonlySet<string> = new Set([
  'apt',
  'apartment',
  'unit',
  'suite',
  'ste',
  'fl',
  'floor',
  'rm',
  'room',
  'bldg',
  'dept',
  'lot',
  'trlr',
  'spc',
  'space',
]);

/** A unit keyword that an ordinal just before it joins: "2nd Floor" is
 *  the unit, as "Floor 2" is. Each is also a UNIT_KEYWORDS entry. */
export const FLOOR_KEYWORDS: ReadonlySet<string> = new Set(['fl', 'floor']);

const DIRECTIONAL_FORMS: ReadonlySet<string> = new Set([...DIRECTIONALS.keys(), ...DIRECTIONALS.values()]);

// ---------------------------------------------------------------------
// Reading Azure's answer
// ---------------------------------------------------------------------

function textOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/** Each entry of a postal code list, split on "," and trimmed, as its
 *  first five digits; an entry that does not start with five digits is
 *  dropped. A missing or non-string value gives []. */
export function matchZips(postalCode: unknown): string[] {
  if (typeof postalCode !== 'string') return [];
  return postalCode
    .split(',')
    .map((p) => p.trim())
    .filter((p) => /^\d{5}/.test(p))
    .map((p) => p.slice(0, 5));
}

/** The first result of an Azure Maps Search Address body, or null when
 *  there is none or it has no finite position. */
export function parseGeocodeMatch(body: unknown): GeocodeMatch | null {
  const results = (body as { results?: unknown } | null | undefined)?.results;
  if (!Array.isArray(results) || results.length === 0) return null;
  const first = results[0] as {
    type?: unknown;
    entityType?: unknown;
    score?: unknown;
    position?: { lat?: unknown; lon?: unknown };
    address?: Record<string, unknown>;
  } | null;
  if (first === null || typeof first !== 'object') return null;
  const lat = first.position?.lat;
  const lon = first.position?.lon;
  if (typeof lat !== 'number' || typeof lon !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }
  const address = first.address ?? {};
  const code = textOrNull(address['countrySubdivisionCode'])?.toUpperCase() ?? null;
  return {
    type: typeof first.type === 'string' ? first.type : null,
    score: typeof first.score === 'number' && Number.isFinite(first.score) ? first.score : null,
    streetNumber: textOrNull(address['streetNumber']),
    streetName: textOrNull(address['streetName']),
    municipality: textOrNull(address['municipality']),
    localName: textOrNull(address['localName']),
    municipalitySubdivision: textOrNull(address['municipalitySubdivision']),
    entityType: textOrNull(first.entityType),
    zips: matchZips(address['postalCode']),
    stateCode: code !== null && isUsStateCode(code) ? code : null,
  };
}

/** One name field split on commas, each part trimmed, blanks dropped. */
function commaParts(field: string | null): string[] {
  return (field ?? '')
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name !== '');
}

/** The town names an answer lists: its municipality, localName and
 *  municipalitySubdivision, in that order, each split on commas, since a
 *  ZIP that covers several towns is answered with them all in one field
 *  ("Andover, Andover Township, Hopatcong"). The placement rule's anchor,
 *  the address check's town comparison and the evaluation script's town
 *  category all read this list, and compare each listed name whole. */
export function listedTownNames(match: GeocodeMatch): string[] {
  return [match.municipality, match.localName, match.municipalitySubdivision].flatMap(commaParts);
}

/** The one town a suggestion names: the first comma-part of localName,
 *  else the first of municipality; null when neither has one. "Did you
 *  mean" offers a town, never a list. */
export function suggestedTownName(match: GeocodeMatch): string | null {
  return commaParts(match.localName)[0] ?? commaParts(match.municipality)[0] ?? null;
}

// ---------------------------------------------------------------------
// Street names
// ---------------------------------------------------------------------

/** Lowercase, `.` `,` `'` removed, whitespace collapsed. */
export function basicStreet(text: string): string {
  return text.toLowerCase().replace(/[.,']/g, '').replace(/\s+/g, ' ').trim();
}

// ---------------------------------------------------------------------
// Unit lines and PO Box lines
// ---------------------------------------------------------------------

/** "2nd", "10th", or a spelled-out ORDINALS word. */
export function isOrdinal(token: string): boolean {
  const t = basicStreet(token);
  return /^\d+(?:st|nd|rd|th)$/.test(t) || ORDINALS.has(t);
}

/** A token's keyword form: lower-cased, a trailing `.` removed. */
function keywordOf(token: string): string {
  return token.toLowerCase().replace(/\.$/, '');
}

/** True when a whole segment is a unit line: its first token is a
 *  UNIT_KEYWORDS entry or starts with `#`, or its first two tokens are an
 *  ordinal and a floor keyword ("Suite 2", "#4", "2nd Floor", "Apt B"). */
export function isUnitSegment(segment: string): boolean {
  const tokens = segment.trim().split(/\s+/).filter((t) => t !== '');
  const first = tokens[0];
  if (first === undefined) return false;
  if (UNIT_KEYWORDS.has(keywordOf(first)) || first.startsWith('#')) return true;
  const second = tokens[1];
  return second !== undefined && isOrdinal(first) && FLOOR_KEYWORDS.has(keywordOf(second));
}

/** The line lower-cased with its dots removed and whitespace collapsed,
 *  as a PO Box line is read. */
const PO_BOX_OPENING = /^(?:p\s*o\s*box|post\s*office\s*box)\b\s*/;

/** The tokens after a PO Box opening ("PO Box", "P.O. Box", "Post Office
 *  Box", "POBox"), lower-cased with dots removed; null when the line
 *  does not open with one. */
export function poBoxTail(line: string): string[] | null {
  const text = line.replace(/\./g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const m = PO_BOX_OPENING.exec(text);
  if (m === null) return null;
  return text
    .slice(m[0].length)
    .split(' ')
    .filter((t) => t !== '');
}

/** True when the line opens with a PO Box opening followed by a token
 *  holding a digit ("PO Box 705", "P.O. Box 12", "Post Office Box 9",
 *  "PO BOX 585", "POBox 3013"); "PO Box", "PO Box A" and "12 PO Box Rd"
 *  are not. */
export function isPoBoxLine(line: string): boolean {
  const tail = poBoxTail(line);
  return tail !== null && tail.length > 0 && /\d/.test(tail[0]!);
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** A route opening, then an optional route word or a hyphen, then a
 *  number of one to four digits with an optional letter, and nothing
 *  after. Longest openings first, so a two-word qualifier wins. */
const ROUTE_SHAPE: RegExp = (() => {
  const openings = [...new Set([...ROUTE_QUALIFIERS, ...ROUTE_LEADS, ...[...US_STATE_CODES].map((c) => c.toLowerCase())])]
    .sort((a, b) => b.length - a.length)
    .map((o) => escapeRe(o).replace(/ /g, '\\s+'));
  const words = [...ROUTE_WORDS].sort((a, b) => b.length - a.length).map(escapeRe);
  const sep = '(?:\\s*-\\s*|\\s+)';
  return new RegExp(`^(?:${openings.join('|')})${sep}(?:(?:${words.join('|')})${sep})?(\\d{1,4}[a-z]?)$`);
})();

function routeShape(name: string): string | null {
  const m = ROUTE_SHAPE.exec(name);
  return m ? `rte ${m[1]}` : null;
}

/**
 * The canonical form of a route name, `rte <number>`, or null when the
 * name is not route-shaped. A trailing directional is removed and
 * discarded when what remains is route-shaped. "Road 46" and "Highway 46"
 * have no opening and are street names; a state code followed by five
 * digits is a state and ZIP, since a route number has at most four.
 */
export function canonicalRouteName(name: string): string | null {
  const s = basicStreet(name);
  const tokens = s.split(' ');
  if (tokens.length > 1 && DIRECTIONAL_FORMS.has(tokens[tokens.length - 1]!)) {
    const withoutDirectional = routeShape(tokens.slice(0, -1).join(' '));
    if (withoutDirectional !== null) return withoutDirectional;
  }
  return routeShape(s);
}
